import { PlanDefinition } from "@medplum/fhirtypes";

import { v4 as uuidv4 } from "uuid";

import {
  QuiltedHealthCodeSystems,
  QuiltedHealthUsageContextTypes,
} from "@/fhir/enums/quilted-health";
import { joinReferenceString } from "@/utils/medplum";

import { TasksFormType } from "../../../../types/checklist-template";
import {
  CHECKLIST_TEMPLATE_SORT_KEYS,
  CHECKLIST_TEMPLATE_SORT_KEYS_MAP,
  ChecklistTemplateShareSettingsOptions,
} from "../constants/checklist-template";
import { ChecklistTemplateFormType } from "../zod-schema/checklist-template-zod";

export const createTemplatePlanDefinition = (
  values: ChecklistTemplateFormType,
  practitionerId: string,
  templateId?: string,
): PlanDefinition => {
  const plantDefinition: PlanDefinition = {
    resourceType: "PlanDefinition",
    ...(templateId ? { id: templateId } : {}),
    status: "active",
    useContext: [
      {
        code: {
          system: QuiltedHealthCodeSystems.UsageContextType,
          code: QuiltedHealthUsageContextTypes.ChecklistTemplates,
          display: "Checklist Templates",
        },
        valueCodeableConcept: {
          coding: [
            {
              system: QuiltedHealthCodeSystems.ChecklistTemplateVisibility,
              code: values.shareSettings,
              display: values.shareSettings,
            },
          ],
        },
      },
    ],
    title: values.name,
    date: new Date().toISOString(),
    action: Object.values(values.tasks).map((task) => ({
      title: task.name,
    })),
    type: {
      coding: [
        {
          system: "http://terminology.hl7.org/CodeSystem/plan-definition-type",
          code: "workflow-definition",
        },
      ],
    },
    meta: {
      author: { reference: joinReferenceString("Practitioner", practitionerId) },
    },
  };

  return plantDefinition;
};

export const getChecklistTemplateInitialValues = (
  defaultValues: ChecklistTemplateFormType,
  template?: PlanDefinition | null,
): ChecklistTemplateFormType => {
  if (!template || !template.action) return defaultValues;

  const tasks: TasksFormType = template.action.reduce<TasksFormType>((acc, action) => {
    const taskId = uuidv4();
    return {
      ...acc,
      [taskId]: {
        name: action.title || "",
        id: taskId,
      },
    };
  }, {});

  const shareSettings =
    (template.useContext?.[0]?.valueCodeableConcept?.coding?.[0]
      ?.code as ChecklistTemplateShareSettingsOptions) ||
    ChecklistTemplateShareSettingsOptions.Public;

  return {
    name: template.title || "",
    shareSettings,
    tasks,
  };
};

export function getChecklistTemplateValidSortKey(
  sortKey: CHECKLIST_TEMPLATE_SORT_KEYS | undefined,
) {
  if (!sortKey) return undefined;

  const parsedSortKey = sortKey.startsWith("-") ? sortKey.slice(1) : sortKey;
  const key = CHECKLIST_TEMPLATE_SORT_KEYS_MAP[parsedSortKey as CHECKLIST_TEMPLATE_SORT_KEYS];
  const isDescending = sortKey?.startsWith("-");
  if (key) {
    return isDescending ? `-${key}` : key;
  }

  return undefined;
}
